import { useCallback, useEffect, useState } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Patient, AllergyIntolerance } from '@medplum/fhirtypes';

export interface AllergiesData {
  readonly allergies: AllergyIntolerance[];
}

export const useAllergiesData = (patient?: Patient, enabled = true) => {
  const medplum = useMedplum();
  const [data, setData] = useState<AllergiesData>({
    allergies: [],
  });
  const [loading, setLoading] = useState(false);

  const fetchAllergies = useCallback(async () => {
    if (!patient?.id) {
      setData({ allergies: [] });
      return;
    }

    medplum.invalidateSearches('AllergyIntolerance');

    setLoading(true);
    try {
      const ref = `Patient/${patient.id}`;
      const searchMeta = { _count: 100, _sort: '-_lastUpdated' };

      const allergies = await medplum.searchResources('AllergyIntolerance', {
        patient: ref,
        ...searchMeta,
      });

      setData({ allergies });
    } catch (error) {
      console.error('Error loading allergies:', error);
    } finally {
      setLoading(false);
    }
  }, [patient?.id, medplum]);

  useEffect(() => {
    if (!enabled) {
      setData({ allergies: [] });
      return;
    }
    fetchAllergies();
  }, [fetchAllergies, enabled]);

  useSubscription(
    enabled && patient?.id
      ? `AllergyIntolerance?patient=Patient/${patient.id}`
      : '',
    () => {
      fetchAllergies();
    }
  );

  return { data, loading, refetch: fetchAllergies };
};
